/**
 * Page URL helpers for description_url and url (C28, §9.1, §17.8).
 */

/**
 * Registrable-ish site key: hostname without a leading "www.".
 * @param {string} hostname
 * @returns {string}
 */
function siteKey(hostname) {
  return String(hostname || '')
    .toLowerCase()
    .replace(/^www\./, '');
}

/**
 * True when candidate is an absolute https URL on the same site as the page.
 * @param {string} candidate
 * @param {string} pageHostname
 * @returns {URL|null} parsed URL when valid
 */
export function parseSameSiteHttps(candidate, pageHostname) {
  if (!candidate) return null;
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch (error) {
    return null; // Relative or malformed: not usable (C28).
  }
  if (parsed.protocol !== 'https:') return null;
  const pageSite = siteKey(pageHostname);
  const candidateSite = siteKey(parsed.hostname);
  if (candidateSite !== pageSite && !candidateSite.endsWith('.' + pageSite)) return null;
  return parsed;
}

/**
 * Resolve the description_url for a slot (C28).
 * Order (assumption A13): the slot's data-qsvp-url (§17.8, needed for infinite scroll),
 * then <link rel="canonical">, both only when absolute https on the same site;
 * else location.origin + location.pathname (no hash, no query, so no gclid/qs_src).
 * Never document.referrer. Returns null when nothing valid exists: no video request.
 * @param {object} input
 * @param {string} [input.slotUrl] data-qsvp-url
 * @param {string} [input.canonicalHref] canonical link href
 * @param {{protocol:string, hostname:string, origin:string, pathname:string}} input.location
 * @returns {string|null} unencoded absolute URL (URLSearchParams encodes it)
 */
export function resolveDescriptionUrl({ slotUrl, canonicalHref, location }) {
  const hostname = location && location.hostname;
  for (const candidate of [slotUrl, canonicalHref]) {
    const parsed = parseSameSiteHttps(candidate, hostname);
    if (parsed) {
      parsed.hash = '';
      return parsed.toString();
    }
  }
  if (!location || location.protocol !== 'https:' || !location.pathname) return null;
  return location.origin + location.pathname;
}

/**
 * Read the canonical link href from a document, if any.
 * @param {Document} doc
 * @returns {string|null}
 */
export function readCanonical(doc) {
  const link = doc && doc.querySelector('link[rel="canonical"]');
  return link ? link.href || link.getAttribute('href') : null;
}
